import { platformLanguages, type PlatformLanguage } from './languages';
import { officialMessages } from './logto-catalog';
import { additionalMessages } from './locale-overlays';

export const en = {
  emailEntry: 'Sign in with email',
  identifierEntry: 'Sign in with your account',
  employeeEntry: 'Employee sign-in',
  employeeHint: 'Continue to your company’s sign-in page with your work account.',
  continuing: 'Continuing…',
  otherMethods: 'or',
  language: 'Language',
  appearance: 'Appearance',
  light: 'Light',
  dark: 'Dark',
  system: 'System',
  workspace: 'Workspace',
  identity: 'Identity platform',
  account: 'Account center',
  signIn: 'Sign in to LEXMOUNT',
  signInHint: 'One account for your connected applications.',
  codeSignIn: 'Sign in with an email code',
  footer: 'LEXMOUNT · Unified identity',
  create: 'Create an account',
  employee: 'Employee SSO',
  preferences: 'Preferences',
  preferenceHint: 'Preferences are saved on this browser.',
  email: 'Email address',
  manage: 'Manage',
  profileTitle: 'Personal information',
  managed: 'Managed by your company',
  readOnly: 'Read only',
  profileDescription: 'Manage the profile shared across your applications.',
  securityLink: 'Manage security',
  registerUsernameNote: 'Create the username and password for your administrator account.',
  registerNote: 'Verify your email, then set a password. You can also sign in with an email code.',
  continueGoogle: 'Continue with Google',
  continueGithub: 'Continue with GitHub',
  pending: 'Not enabled',
  pendingHint:
    'This sign-in method will be available after your administrator configures and enables it.',
  back: 'Back to your app ↗',
  details: 'Your profile',
  shared: 'Changes apply to all connected apps.',
  avatar: 'Avatar',
  avatarUrl: 'HTTPS avatar URL',
  avatarEdit: 'Change avatar',
  avatarSave: 'Save avatar',
  avatarHelp: 'Use an HTTPS image URL, or leave it empty to remove your avatar.',
  avatarInvalid: 'Enter a valid HTTPS image URL without credentials.',
  avatarBroken: 'Image unavailable',
  unset: 'Not set',
  savePending: 'Saving…',
  cancel: 'Cancel',
  updateError: 'Could not save. Please try again.',
  employeeManaged:
    'Your company manages your email and password. Contact your administrator to change them.',
  clearInput: 'Clear input',
  continueWith: 'Continue with {name}',
  languageError: 'Could not change the language. Please try again.',
};
export const zh: typeof en = {
  emailEntry: '邮箱登录',
  identifierEntry: '账号登录',
  employeeEntry: '员工登录',
  employeeHint: '使用公司账号，前往企业身份平台完成认证。',
  continuing: '正在跳转…',
  otherMethods: '或使用其他方式',
  language: '语言',
  appearance: '外观',
  light: '浅色',
  dark: '深色',
  system: '跟随系统',
  workspace: '工作台',
  identity: '统一身份平台',
  account: '账号中心',
  signIn: '登录 LEXMOUNT',
  signInHint: '一个账号，访问已接入的应用。',
  codeSignIn: '使用邮箱验证码登录',
  footer: 'LEXMOUNT · 统一身份平台',
  create: '创建账号',
  employee: '员工 SSO 登录',
  preferences: '偏好设置',
  preferenceHint: '偏好设置保存在当前浏览器中。',
  email: '邮箱地址',
  manage: '管理',
  profileTitle: '个人资料',
  managed: '由公司管理',
  readOnly: '只读',
  profileDescription: '集中维护各个应用使用的个人资料。',
  securityLink: '管理账号安全',
  registerUsernameNote: '创建管理员用户名，并设置登录密码。',
  registerNote: '验证邮箱后设置密码。注册完成后，也可以使用邮箱验证码登录。',
  continueGoogle: '使用 Google 继续',
  continueGithub: '使用 GitHub 继续',
  pending: '未启用',
  pendingHint: '管理员配置并启用后，即可使用此登录方式。',
  back: '返回原网站 ↗',
  details: '个人资料',
  shared: '资料变更将同步到所有接入的应用。',
  avatar: '头像',
  avatarUrl: 'HTTPS 头像链接',
  avatarEdit: '更改头像',
  avatarSave: '保存头像',
  avatarHelp: '填写 HTTPS 图片链接；留空保存即可移除头像。',
  avatarInvalid: '请输入不含用户名或密码的有效 HTTPS 头像链接',
  avatarBroken: '图片暂不可用',
  unset: '未设置',
  savePending: '保存中…',
  cancel: '取消',
  updateError: '更新失败，请稍后重试',
  employeeManaged: '公司统一管理你的邮箱和密码。如需修改，请联系管理员。',
  clearInput: '清空输入',
  continueWith: '使用 {name} 继续',
  languageError: '语言切换失败，请重试。',
};
const es: typeof en = {
  emailEntry: 'Iniciar sesión con correo',
  identifierEntry: 'Iniciar sesión con tu cuenta',
  employeeEntry: 'Acceso de empleados',
  employeeHint: 'Continúa a la página de acceso de tu empresa con tu cuenta de trabajo.',
  continuing: 'Continuando…',
  otherMethods: 'o',
  language: 'Idioma',
  appearance: 'Apariencia',
  light: 'Claro',
  dark: 'Oscuro',
  system: 'Sistema',
  workspace: 'Espacio de trabajo',
  identity: 'Plataforma de identidad',
  account: 'Centro de cuenta',
  signIn: 'Iniciar sesión en LEXMOUNT',
  signInHint: 'Una cuenta para tus aplicaciones conectadas.',
  codeSignIn: 'Usar un código por correo',
  footer: 'LEXMOUNT · Identidad unificada',
  create: 'Crear una cuenta',
  employee: 'SSO de empleados',
  preferences: 'Preferencias',
  preferenceHint: 'Las preferencias se guardan en este navegador.',
  email: 'Correo electrónico',
  manage: 'Administrar',
  profileTitle: 'Información personal',
  managed: 'Administrado por tu empresa',
  readOnly: 'Solo lectura',
  profileDescription: 'Gestiona el perfil compartido entre tus aplicaciones.',
  securityLink: 'Gestionar seguridad',
  registerUsernameNote:
    'Crea un nombre de usuario y una contraseña para tu cuenta de administrador.',
  registerNote:
    'Verifica tu correo y crea una contraseña. También podrás acceder con un código por correo.',
  continueGoogle: 'Continuar con Google',
  continueGithub: 'Continuar con GitHub',
  pending: 'No habilitado',
  pendingHint: 'Este método estará disponible cuando el administrador lo configure y habilite.',
  back: 'Volver a tu aplicación ↗',
  details: 'Tu perfil',
  shared: 'Los cambios se aplican a todas las aplicaciones conectadas.',
  avatar: 'Avatar',
  avatarUrl: 'URL HTTPS del avatar',
  avatarEdit: 'Cambiar avatar',
  avatarSave: 'Guardar avatar',
  avatarHelp: 'Usa una URL de imagen HTTPS o deja el campo vacío para eliminar el avatar.',
  avatarInvalid: 'Introduce una URL HTTPS válida sin credenciales.',
  avatarBroken: 'Imagen no disponible',
  unset: 'Sin configurar',
  savePending: 'Guardando…',
  cancel: 'Cancelar',
  updateError: 'No se pudo guardar. Inténtalo de nuevo.',
  employeeManaged:
    'Tu empresa administra tu correo y contraseña. Contacta con el administrador para cambiarlos.',
  clearInput: 'Borrar entrada',
  continueWith: 'Continuar con {name}',
  languageError: 'No se pudo cambiar el idioma. Inténtalo de nuevo.',
};
const fr: typeof en = {
  emailEntry: 'Connexion par e-mail',
  identifierEntry: 'Connexion avec votre compte',
  employeeEntry: 'Accès des employés',
  employeeHint:
    'Accédez à la page de connexion de votre entreprise avec votre compte professionnel.',
  continuing: 'Redirection…',
  otherMethods: 'ou',
  language: 'Langue',
  appearance: 'Apparence',
  light: 'Clair',
  dark: 'Sombre',
  system: 'Système',
  workspace: 'Espace de travail',
  identity: 'Plateforme d’identité',
  account: 'Centre de compte',
  signIn: 'Connexion à LEXMOUNT',
  signInHint: 'Un compte pour vos applications connectées.',
  codeSignIn: 'Utiliser un code par e-mail',
  footer: 'LEXMOUNT · Identité unifiée',
  create: 'Créer un compte',
  employee: 'SSO des employés',
  preferences: 'Préférences',
  preferenceHint: 'Les préférences sont enregistrées dans ce navigateur.',
  email: 'Adresse e-mail',
  manage: 'Gérer',
  profileTitle: 'Informations personnelles',
  managed: 'Géré par votre entreprise',
  readOnly: 'Lecture seule',
  profileDescription: 'Gérez le profil partagé entre vos applications.',
  securityLink: 'Gérer la sécurité',
  registerUsernameNote: 'Créez un identifiant et un mot de passe pour votre compte administrateur.',
  registerNote:
    'Vérifiez votre e-mail, puis définissez un mot de passe. Vous pourrez aussi utiliser un code par e-mail.',
  continueGoogle: 'Continuer avec Google',
  continueGithub: 'Continuer avec GitHub',
  pending: 'Non activé',
  pendingHint:
    'Cette méthode sera disponible après sa configuration et son activation par l’administrateur.',
  back: 'Retour à votre application ↗',
  details: 'Votre profil',
  shared: 'Les modifications s’appliquent à toutes les applications connectées.',
  avatar: 'Avatar',
  avatarUrl: 'URL HTTPS de l’avatar',
  avatarEdit: 'Modifier l’avatar',
  avatarSave: 'Enregistrer l’avatar',
  avatarHelp: 'Utilisez une URL d’image HTTPS ou laissez le champ vide pour supprimer l’avatar.',
  avatarInvalid: 'Saisissez une URL HTTPS valide sans identifiants.',
  avatarBroken: 'Image indisponible',
  unset: 'Non défini',
  savePending: 'Enregistrement…',
  cancel: 'Annuler',
  updateError: 'Enregistrement impossible. Réessayez.',
  employeeManaged:
    'Votre entreprise gère votre e-mail et votre mot de passe. Contactez votre administrateur pour les modifier.',
  clearInput: 'Effacer la saisie',
  continueWith: 'Continuer avec {name}',
  languageError: 'Impossible de changer la langue. Réessayez.',
};
const pt: typeof en = {
  emailEntry: 'Entrar com e-mail',
  identifierEntry: 'Entrar com sua conta',
  employeeEntry: 'Acesso de funcionários',
  employeeHint: 'Acesse a página de login da empresa com sua conta de trabalho.',
  continuing: 'Continuando…',
  otherMethods: 'ou',
  language: 'Idioma',
  appearance: 'Aparência',
  light: 'Claro',
  dark: 'Escuro',
  system: 'Sistema',
  workspace: 'Espaço de trabalho',
  identity: 'Plataforma de identidade',
  account: 'Central da conta',
  signIn: 'Entrar no LEXMOUNT',
  signInHint: 'Uma conta para seus aplicativos conectados.',
  codeSignIn: 'Entrar com código por e-mail',
  footer: 'LEXMOUNT · Identidade unificada',
  create: 'Criar uma conta',
  employee: 'SSO de funcionários',
  preferences: 'Preferências',
  preferenceHint: 'As preferências são salvas neste navegador.',
  email: 'Endereço de e-mail',
  manage: 'Gerenciar',
  profileTitle: 'Informações pessoais',
  managed: 'Gerenciado pela empresa',
  readOnly: 'Somente leitura',
  profileDescription: 'Gerencie o perfil compartilhado entre seus aplicativos.',
  securityLink: 'Gerenciar segurança',
  registerUsernameNote: 'Crie um nome de usuário e uma senha para sua conta de administrador.',
  registerNote:
    'Verifique seu e-mail e defina uma senha. Você também poderá entrar com um código por e-mail.',
  continueGoogle: 'Continuar com Google',
  continueGithub: 'Continuar com GitHub',
  pending: 'Não habilitado',
  pendingHint: 'Este método estará disponível após a configuração e ativação pelo administrador.',
  back: 'Voltar ao aplicativo ↗',
  details: 'Seu perfil',
  shared: 'As alterações se aplicam a todos os aplicativos conectados.',
  avatar: 'Avatar',
  avatarUrl: 'URL HTTPS do avatar',
  avatarEdit: 'Alterar avatar',
  avatarSave: 'Salvar avatar',
  avatarHelp: 'Use uma URL de imagem HTTPS ou deixe o campo vazio para remover o avatar.',
  avatarInvalid: 'Insira uma URL HTTPS válida sem credenciais.',
  avatarBroken: 'Imagem indisponível',
  unset: 'Não definido',
  savePending: 'Salvando…',
  cancel: 'Cancelar',
  updateError: 'Não foi possível salvar. Tente novamente.',
  employeeManaged:
    'Sua empresa gerencia seu e-mail e senha. Contate o administrador para alterá-los.',
  clearInput: 'Limpar entrada',
  continueWith: 'Continuar com {name}',
  languageError: 'Não foi possível alterar o idioma. Tente novamente.',
};
const de: typeof en = {
  emailEntry: 'Mit E-Mail anmelden',
  identifierEntry: 'Mit Ihrem Konto anmelden',
  employeeEntry: 'Mitarbeiteranmeldung',
  employeeHint: 'Mit Ihrem Arbeitskonto zur Anmeldeseite Ihres Unternehmens wechseln.',
  continuing: 'Weiterleitung…',
  otherMethods: 'oder',
  language: 'Sprache',
  appearance: 'Darstellung',
  light: 'Hell',
  dark: 'Dunkel',
  system: 'System',
  workspace: 'Arbeitsbereich',
  identity: 'Identitätsplattform',
  account: 'Kontocenter',
  signIn: 'Bei LEXMOUNT anmelden',
  signInHint: 'Ein Konto für Ihre verbundenen Anwendungen.',
  codeSignIn: 'Mit E-Mail-Code anmelden',
  footer: 'LEXMOUNT · Einheitliche Identität',
  create: 'Konto erstellen',
  employee: 'Mitarbeiter-SSO',
  preferences: 'Einstellungen',
  preferenceHint: 'Die Einstellungen werden in diesem Browser gespeichert.',
  email: 'E-Mail-Adresse',
  manage: 'Verwalten',
  profileTitle: 'Persönliche Informationen',
  managed: 'Vom Unternehmen verwaltet',
  readOnly: 'Schreibgeschützt',
  profileDescription: 'Verwalten Sie Ihr gemeinsames Profil für alle Anwendungen.',
  securityLink: 'Sicherheit verwalten',
  registerUsernameNote:
    'Erstellen Sie einen Benutzernamen und ein Passwort für Ihr Administratorkonto.',
  registerNote:
    'Bestätigen Sie Ihre E-Mail und legen Sie ein Passwort fest. Sie können sich auch mit einem E-Mail-Code anmelden.',
  continueGoogle: 'Mit Google fortfahren',
  continueGithub: 'Mit GitHub fortfahren',
  pending: 'Nicht aktiviert',
  pendingHint:
    'Diese Methode ist verfügbar, sobald ein Administrator sie konfiguriert und aktiviert hat.',
  back: 'Zurück zur Anwendung ↗',
  details: 'Ihr Profil',
  shared: 'Änderungen gelten für alle verbundenen Anwendungen.',
  avatar: 'Profilbild',
  avatarUrl: 'HTTPS-URL des Profilbilds',
  avatarEdit: 'Profilbild ändern',
  avatarSave: 'Profilbild speichern',
  avatarHelp:
    'Verwenden Sie eine HTTPS-Bild-URL oder lassen Sie das Feld leer, um das Profilbild zu entfernen.',
  avatarInvalid: 'Geben Sie eine gültige HTTPS-URL ohne Zugangsdaten ein.',
  avatarBroken: 'Bild nicht verfügbar',
  unset: 'Nicht festgelegt',
  savePending: 'Wird gespeichert…',
  cancel: 'Abbrechen',
  updateError: 'Speichern fehlgeschlagen. Bitte erneut versuchen.',
  employeeManaged:
    'Ihr Unternehmen verwaltet Ihre E-Mail und Ihr Passwort. Wenden Sie sich für Änderungen an Ihren Administrator.',
  clearInput: 'Eingabe löschen',
  continueWith: 'Mit {name} fortfahren',
  languageError: 'Sprache konnte nicht geändert werden. Bitte erneut versuchen.',
};
const ja: typeof en = {
  emailEntry: 'メールでログイン',
  identifierEntry: 'アカウントでログイン',
  employeeEntry: '従業員ログイン',
  employeeHint: '会社のアカウントで企業のログインページに進みます。',
  continuing: '移動中…',
  otherMethods: 'または',
  language: '言語',
  appearance: '表示',
  light: 'ライト',
  dark: 'ダーク',
  system: 'システム',
  workspace: 'ワークスペース',
  identity: 'ID プラットフォーム',
  account: 'アカウントセンター',
  signIn: 'LEXMOUNT にログイン',
  signInHint: '1 つのアカウントで連携アプリを利用できます。',
  codeSignIn: 'メール認証コードでログイン',
  footer: 'LEXMOUNT · 統合 ID',
  create: 'アカウントを作成',
  employee: '従業員 SSO',
  preferences: '設定',
  preferenceHint: '設定はこのブラウザーに保存されます。',
  email: 'メールアドレス',
  manage: '管理',
  profileTitle: '個人情報',
  managed: '会社が管理',
  readOnly: '読み取り専用',
  profileDescription: '各アプリで共有するプロフィールを管理します。',
  securityLink: 'セキュリティを管理',
  registerUsernameNote: '管理者アカウントのユーザー名とパスワードを設定してください。',
  registerNote:
    'メールを確認してパスワードを設定します。登録後はメール認証コードでもログインできます。',
  continueGoogle: 'Google で続行',
  continueGithub: 'GitHub で続行',
  pending: '未有効化',
  pendingHint: '管理者が設定して有効にすると、このログイン方法を利用できます。',
  back: 'アプリに戻る ↗',
  details: 'プロフィール',
  shared: '変更はすべての連携アプリに反映されます。',
  avatar: 'アバター',
  avatarUrl: 'アバターの HTTPS URL',
  avatarEdit: 'アバターを変更',
  avatarSave: 'アバターを保存',
  avatarHelp: 'HTTPS 画像 URL を入力してください。空欄で保存するとアバターを削除できます。',
  avatarInvalid: '認証情報を含まない有効な HTTPS URL を入力してください。',
  avatarBroken: '画像を表示できません',
  unset: '未設定',
  savePending: '保存中…',
  cancel: 'キャンセル',
  updateError: '保存できませんでした。もう一度お試しください。',
  employeeManaged:
    'メールとパスワードは会社が管理しています。変更するには管理者にお問い合わせください。',
  clearInput: '入力をクリア',
  continueWith: '{name} で続行',
  languageError: '言語を変更できませんでした。もう一度お試しください。',
};

const existing: Partial<Record<PlatformLanguage, typeof en>> = { en, zh, es, fr, pt, ja, de };
const additions: Partial<Record<PlatformLanguage, Partial<typeof en>>> = {
  ...additionalMessages,
  'zh-HK': {
    ...additionalMessages['zh-TW'],
    identity: '統一身份平台',
    footer: 'LEXMOUNT · 統一身份',
    readOnly: '唯讀',
    signInHint: '使用同一帳號存取所有已連接的應用程式。',
  },
};

export const messages = Object.fromEntries(
  platformLanguages.map(({ code }) => {
    const regionalBase = code === 'es-MX' ? es : code === 'pt-PT' ? pt : undefined;
    const values = {
      ...en,
      ...regionalBase,
      ...officialMessages[code],
      ...existing[code],
      ...additions[code],
    };
    // Keep the same custom-copy schema in every language; welcome-only fields stay in the portal.
    return [
      code,
      Object.fromEntries(Object.keys(en).map((key) => [key, values[key as keyof typeof en]])),
    ];
  }),
) as Record<PlatformLanguage, typeof en>;
